import { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar as BigCalendar } from "react-big-calendar";
import { parseISO } from "date-fns";
import { dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { Job } from "../types/database";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Custom CSS for the calendar
import "./Calendar.css";

// Create a date localizer using date-fns
const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Define view types
type ViewType = "month" | "week" | "day" | "agenda";

// Define event display types
type EventDisplayType = "all" | "prep" | "ship" | "return" | "duration";

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  status: string;
  projectNumber: string;
  allDay?: boolean;
  resource?: any;
}

export default function Calendar() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [view, setView] = useState<ViewType>("month");
  const [date, setDate] = useState(new Date());
  const [eventDisplay, setEventDisplay] =
    useState<EventDisplayType>("duration");

  // Fetch jobs data
  const {
    data: jobs = [],
    isLoading,
    error,
  } = useQuery<Job[]>({
    queryKey: ["jobs", currentOrganization?.id],
    queryFn: () => jobService.getJobs(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Convert jobs to calendar events based on the selected display type
  const events = useMemo(() => {
    return jobs.map((job) => {
      let start: Date;
      let end: Date;
      let allDay = true;

      switch (eventDisplay) {
        case "prep":
          start = parseISO(job.prep_date);
          end = parseISO(job.prep_date);
          break;
        case "ship":
          start = parseISO(job.ship_date);
          end = parseISO(job.ship_date);
          break;
        case "return":
          start = parseISO(job.return_date);
          end = parseISO(job.return_date);
          break;
        case "duration":
        default:
          start = parseISO(job.prep_date);
          end = parseISO(job.return_date);
          break;
      }

      return {
        id: job.id,
        title: job.title,
        start,
        end,
        status: job.status,
        projectNumber: job.project_number,
        allDay,
      };
    });
  }, [jobs, eventDisplay]);

  // Handle event click to navigate to job details
  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      navigate(`/org/${orgId}/jobs/${event.id}`);
    },
    [navigate, orgId]
  );

  // Custom event styling based on job status
  const eventPropGetter = useCallback((event: CalendarEvent) => {
    let backgroundColor = "";
    let borderColor = "";
    let opacity = 0.9;

    switch (event.status) {
      case "upcoming":
        backgroundColor = "#FEF9C3"; // yellow-100
        borderColor = "#CA8A04"; // yellow-600
        break;
      case "in_progress":
        backgroundColor = "#DBEAFE"; // blue-100
        borderColor = "#2563EB"; // blue-600
        break;
      case "completed":
        backgroundColor = "#DCFCE7"; // green-100
        borderColor = "#16A34A"; // green-600
        opacity = 0.8;
        break;
      default:
        backgroundColor = "#F3F4F6"; // gray-100
        borderColor = "#6B7280"; // gray-500
    }

    return {
      style: {
        backgroundColor,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: "6px",
        color: "#1F2937", // gray-800
        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        opacity,
      },
    };
  }, []);

  // Custom toolbar to handle date navigation and view changes
  const CustomToolbar = ({ label, onNavigate, onView }: any) => {
    return (
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4 p-3 bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="flex space-x-2">
          <button
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            onClick={() => onNavigate("TODAY")}
          >
            Today
          </button>
          <button
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            onClick={() => onNavigate("PREV")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            onClick={() => onNavigate("NEXT")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        <h2 className="text-lg font-semibold text-gray-800">{label}</h2>
        <div className="flex space-x-1">
          <button
            className={`px-3 py-1.5 border rounded-md transition-colors ${
              view === "month"
                ? "bg-primary text-white border-primary"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
            onClick={() => onView("month")}
          >
            Month
          </button>
          <button
            className={`px-3 py-1.5 border rounded-md transition-colors ${
              view === "week"
                ? "bg-primary text-white border-primary"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
            onClick={() => onView("week")}
          >
            Week
          </button>
          <button
            className={`px-3 py-1.5 border rounded-md transition-colors ${
              view === "day"
                ? "bg-primary text-white border-primary"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
            onClick={() => onView("day")}
          >
            Day
          </button>
          <button
            className={`px-3 py-1.5 border rounded-md transition-colors ${
              view === "agenda"
                ? "bg-primary text-white border-primary"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
            onClick={() => onView("agenda")}
          >
            Agenda
          </button>
        </div>
      </div>
    );
  };

  // Custom event component to show project number and title
  const EventComponent = ({ event }: { event: CalendarEvent }) => (
    <div className="p-1.5 overflow-hidden h-full flex flex-col justify-between">
      <div className="text-xs font-semibold">{event.projectNumber}</div>
      <div className="text-sm truncate font-medium">{event.title}</div>
    </div>
  );

  // Custom day header component
  const DayHeaderFormat = ({ date, label }: { date: Date; label: string }) => (
    <div className="text-center">
      <div className="text-xs text-gray-500 uppercase">
        {format(date, "EEE")}
      </div>
      <div className="text-sm font-medium">{label}</div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          Error loading jobs. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your job schedule
          </p>
        </div>
      </div>

      {/* Display Options */}
      <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Mode
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            value={eventDisplay}
            onChange={(e) =>
              setEventDisplay(e.target.value as EventDisplayType)
            }
          >
            <option value="duration">Full Job Duration</option>
            <option value="prep">Prep Dates Only</option>
            <option value="ship">Ship Dates Only</option>
            <option value="return">Return Dates Only</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Legend
          </label>
          <div className="flex gap-3 py-2">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-yellow-100 border border-yellow-600"></div>
              <span className="text-xs text-gray-600">Upcoming</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-blue-100 border border-blue-600"></div>
              <span className="text-xs text-gray-600">In Progress</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-green-100 border border-green-600"></div>
              <span className="text-xs text-gray-600">Completed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 h-[700px]">
        <BigCalendar
          localizer={localizer as any}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
          views={{
            month: true,
            week: true,
            day: true,
            agenda: true,
          }}
          view={view}
          date={date}
          onView={(newView) => setView(newView as ViewType)}
          onNavigate={(newDate) => setDate(newDate)}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventPropGetter}
          components={{
            toolbar: CustomToolbar,
            event: EventComponent,
            day: {
              header: DayHeaderFormat,
            },
          }}
          popup
          selectable
        />
      </div>
    </div>
  );
}
